import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Calendar, Phone, Mail, Globe, MapPin, Download,
  ExternalLink, User, Facebook, Linkedin, ShieldCheck,
  CheckCircle2, AlertCircle, RefreshCw
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function PublicBusinessCard() {
  const { userId } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    fetch(`${API_BASE}/user/vcard/${encodeURIComponent(userId)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!alive) return;
        if (!data.ok) {
          setError(data.error || 'Contact not found');
        } else {
          setAgent(data.agent);
        }
      })
      .catch((err) => {
        if (alive) setError(err.message || 'Error loading contact card');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-white/70">
          <RefreshCw className="w-5 h-5 animate-spin text-purple-400" />
          <span>Loading Digital Business Card...</span>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-800/80 border border-slate-700 rounded-2xl p-6 text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto" />
          <h2 className="text-xl font-bold text-white">Digital Business Card Not Found</h2>
          <p className="text-sm text-slate-400">
            {error || 'The requested agent contact card does not exist or has been removed.'}
          </p>
        </div>
      </div>
    );
  }

  const vcard = agent.vcard || {};
  const scheduler = agent.scheduler || {};
  const addr = vcard.address || {};
  const socials = vcard.socials || {};
  const appearance = vcard.appearance || {};

  const waveStart = appearance.waveStart || '#6D28D9';
  const waveEnd = appearance.waveEnd || '#4C1D95';

  const first = vcard.firstName || agent.name?.split(' ')[0] || '';
  const last = vcard.lastName || agent.name?.split(' ').slice(1).join(' ') || '';
  const displayName = `${first} ${last}`.trim() || agent.name || 'Insurance Agent';
  const title = vcard.title || 'Licensed Agent';
  const org = vcard.organization || 'CIVIX Insurance Group';
  const phone = vcard.cellPhone || agent.phone || '';
  const email = vcard.workEmail || agent.email || '';
  const workUrl = vcard.workUrl || '';
  const licenseTag = vcard.licenseInfo || vcard.nationalId
    ? (vcard.licenseInfo ? `License: #${vcard.licenseInfo}` : `National ID: #${vcard.nationalId}`)
    : 'Licensed Insurance Professional';

  const fullStreet = [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  const mapsUrl = fullStreet ? `https://maps.google.com/?q=${encodeURIComponent(fullStreet)}` : null;

  const bookingUrl = agent.bookingLink || `/schedule/${scheduler.bookingSlug || agent._id}`;
  const vcfUrl = agent.vcfDownloadUrl || `${API_BASE}/user/vcard/${agent._id}/vcf`;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center items-center p-4 sm:p-6 antialiased">
      {/* Container card */}
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 relative">
        {/* Top Wave Header */}
        <div
          className="relative pt-10 pb-0 text-center flex flex-col items-center justify-center overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${waveStart} 0%, ${waveEnd} 100%)`,
          }}
        >
          {/* Avatar */}
          <div className="relative z-10 mb-3">
            {agent.avatar ? (
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-2xl mx-auto bg-white">
                <img
                  src={agent.avatar}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-28 h-28 rounded-full bg-white/20 border-4 border-white shadow-2xl mx-auto flex items-center justify-center text-white">
                <User className="w-14 h-14" />
              </div>
            )}
          </div>

          {/* Curved SVG Wave cutting smoothly into the white bottom half */}
          <svg
            viewBox="0 0 500 120"
            preserveAspectRatio="none"
            className="w-full h-16 -mb-1 fill-white z-0"
          >
            <path d="M0,0 C150,90 350,-40 500,45 L500,120 L0,120 Z" />
          </svg>
        </div>

        {/* Card Body */}
        <div className="px-6 pb-8 pt-1 text-left bg-white text-gray-900 space-y-5">
          {/* Name & Title Block with Vertical Accent Line */}
          <div className="flex items-start">
            <div
              className="w-1.5 rounded-full mr-3.5 self-stretch min-h-[48px]"
              style={{ backgroundColor: waveStart }}
            />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight">
                {displayName}
              </h1>
              <p className="text-sm font-semibold text-gray-700">{title}</p>
              <p className="text-xs italic text-gray-500">{org}</p>
            </div>
          </div>

          {/* License & Credential Pill */}
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-purple-900 bg-purple-50 border border-purple-200">
              <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
              {licenseTag}
            </span>
          </div>

          {/* Bio / Notes */}
          {vcard.notes && (
            <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-xl border border-gray-100">
              {vcard.notes}
            </p>
          )}

          {/* Quick Contact Buttons Row */}
          <div className="grid grid-cols-2 gap-2.5 pt-1">
            {phone && (
              <a
                href={`tel:${phone.replace(/\D/g, '')}`}
                className="flex items-center justify-center gap-2 py-2.5 px-3 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-xl text-xs font-bold transition border border-purple-100"
              >
                <Phone className="w-4 h-4 text-purple-700" />
                Call Agent
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex items-center justify-center gap-2 py-2.5 px-3 bg-purple-50 hover:bg-purple-100 text-purple-900 rounded-xl text-xs font-bold transition border border-purple-100"
              >
                <Mail className="w-4 h-4 text-purple-700" />
                Email
              </a>
            )}
          </div>

          {/* Detailed Contact List */}
          <div className="space-y-3 pt-2 text-xs text-gray-700 border-t border-gray-100">
            {phone && (
              <div className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm"
                  style={{ backgroundColor: waveStart }}
                >
                  <Phone className="w-3.5 h-3.5" />
                </div>
                <a href={`tel:${phone}`} className="truncate font-medium hover:underline">
                  {phone}
                </a>
              </div>
            )}

            {email && (
              <div className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm"
                  style={{ backgroundColor: waveStart }}
                >
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <a href={`mailto:${email}`} className="truncate font-medium hover:underline">
                  {email}
                </a>
              </div>
            )}

            {workUrl && (
              <div className="flex items-center gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm"
                  style={{ backgroundColor: waveStart }}
                >
                  <Globe className="w-3.5 h-3.5" />
                </div>
                <a
                  href={workUrl.startsWith('http') ? workUrl : `https://${workUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate font-medium hover:underline flex items-center gap-1"
                >
                  {workUrl.replace(/^https?:\/\//, '')}
                  <ExternalLink className="w-3 h-3 text-gray-400" />
                </a>
              </div>
            )}

            {fullStreet && (
              <div className="flex items-start gap-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 shadow-sm mt-0.5"
                  style={{ backgroundColor: waveStart }}
                >
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                {mapsUrl ? (
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-gray-700 hover:underline">
                    {addr.label ? <span className="font-semibold block">{addr.label}</span> : null}
                    <span>{fullStreet}</span>
                  </a>
                ) : (
                  <div>
                    {addr.label ? <span className="font-semibold block">{addr.label}</span> : null}
                    <span>{fullStreet}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Socials */}
          {(socials.facebook || socials.linkedin) && (
            <div className="flex items-center justify-center gap-3 pt-1">
              {socials.facebook && (
                <a
                  href={socials.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                  title="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {socials.linkedin && (
                <a
                  href={socials.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
                  title="LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
            </div>
          )}

          {/* Primary & Secondary Call to Actions */}
          <div className="pt-3 space-y-2.5">
            <a
              href={bookingUrl}
              className="w-full py-3 px-4 rounded-xl text-white text-sm font-bold text-center block shadow-lg transition hover:opacity-95"
              style={{
                background: `linear-gradient(135deg, ${waveStart} 0%, ${waveEnd} 100%)`,
              }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                Book an Appointment
              </span>
            </a>

            <a
              href={vcfUrl}
              download
              className="w-full py-2.5 px-4 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold text-center block transition shadow-sm"
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <Download className="w-3.5 h-3.5" />
                Save Contact to Phone (.vcf)
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Powered by CIVIX branding footer */}
      <div className="mt-6 text-center text-xs text-slate-500">
        Powered by <span className="font-semibold text-slate-400">CIVIX CRM</span>
      </div>
    </div>
  );
}
